import { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, DatePicker, Tag, Space, message, InputNumber, Divider, List, Avatar, Upload, Typography, Popover, Checkbox, Dropdown, Popconfirm, Collapse, Calendar, Badge, Row, Col, Card } from 'antd';
import { PlusOutlined, UserOutlined, PaperClipOutlined, LinkOutlined, FileSearchOutlined, SettingOutlined, FilePdfOutlined, SearchOutlined, CalendarOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { useFileViewer } from '../store/FileViewerContext';
import LinkifyText from '../components/LinkifyText';
import TaskChecklist from '../components/TaskChecklist';
import TaskBPM from '../components/TaskBPM';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { TasksService } from '../api/services/tasks.service';
import { CalendarService } from '../api/services/calendar.service';
import { ProjectsService } from '../api/services/projects.service';
import { UsersService } from '../api/services/users.service';
import api from '../api/client';
import FilePickerModal from '../components/FilePickerModal';
import SmartMentionsInput from '../components/SmartMentionsInput';
import GanttChartWrapper from '../components/GanttChartWrapper';
import UniversalFileUploadModal from '../components/UniversalFileUploadModal';
import TaskDetailModal from '../components/TaskDetailModal';
import TaskCreateModal from '../components/TaskCreateModal';

const statusOptions = [
  { value: 'new', label: 'Новая' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'review', label: 'На проверке' },
  { value: 'done', label: 'Готово' },
  { value: 'cancelled', label: 'Отменена' },
];

const priorityOptions = [
  { value: 'low', label: 'Низкий' },
  { value: 'medium', label: 'Средний' },
  { value: 'high', label: 'Высокий' },
  { value: 'critical', label: 'Критический' },
];

const taskTypeOptions = [
  { value: 'land_legal', label: 'Земельно-правовые' },
  { value: 'permissive', label: 'Разрешительные' },
  { value: 'expert', label: 'Экспертные' },
  { value: 'design_engineering', label: 'Проектно-инженерные' },
  { value: 'financial_estimate', label: 'Финансово-сметные' },
  { value: 'control_supervision', label: 'Контрольно-надзорные' },
  { value: 'logistics', label: 'Логистические' },
  { value: 'coordination', label: 'Координационные' },
  { value: 'sales_marketing', label: 'Продажно-маркетинговые' },
  { value: 'cadastral_registration', label: 'Кадастрово-регистрационные' },
  { value: 'warranty', label: 'Гарантийные (постстроительные)' },
  { value: 'judicial_claim', label: 'Судебно-претензионные' },
  { value: 'mobilization', label: 'Мобилизационные (ЧС/форс-мажор)' },
  { value: 'acceptance_transfer', label: 'Приемо-передаточные (УК)' },
  { value: 'organizational_regime', label: 'Организационно-режимные' },
  { value: 'household_administrative', label: 'Хозяйственно-административные (АХО)' },
  { value: 'gr_tasks', label: 'GR-задачи (административное лоббирование)' },
  { value: 'commercial_negotiations', label: 'Коммерческие переговоры с партнёрами' },
];

const dependencyTypeOptions = [
  { value: 'finish_to_start', label: 'Завершение → Начало (FS)' },
  { value: 'start_to_start', label: 'Начало → Начало (SS)' },
  { value: 'finish_to_finish', label: 'Завершение → Завершение (FF)' },
  { value: 'start_to_finish', label: 'Начало → Завершение (SF)' },
];

const participantRoleOptions = [
  { value: 'watcher', label: 'Наблюдатель' },
  { value: 'validator', label: 'Валидатор' },
  { value: 'approver', label: 'Утверждающий' },
  { value: 'curator', label: 'Куратор' },
  { value: 'assignee', label: 'Исполнитель' },
];

export default function TasksPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { openFileCard } = useFileViewer();
  const extractFileId = (url) => {
    const match = url?.match(/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i);
    return match ? match[1] : null;
  };
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Task Modals
  const [modalOpen, setModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [parentTask, setParentTask] = useState(null);
  const [creating, setCreating] = useState(false);
  
  // Project Modals
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [editProjectModalOpen, setEditProjectModalOpen] = useState(false);
  const [projectDetailModalOpen, setProjectDetailModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectCommentText, setProjectCommentText] = useState('');
  const [projectCommentFile, setProjectCommentFile] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('tasks_table_columns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing tasks_table_columns from localStorage', e);
      }
    }
    return ['title', 'task_type', 'project', 'assignee', 'curator_id', 'status', 'priority', 'progress', 'due_date', 'estimated_hours', 'budget', 'updated_at'];
  });
  const [showCompletedTasks, setShowCompletedTasks] = useState(() => {
    const saved = localStorage.getItem('tasks_show_completed');
    return saved === 'true';
  });

  const [projectForm] = Form.useForm();
  const [form] = Form.useForm();
  const [editProjectForm] = Form.useForm();
  const [viewMode, setViewMode] = useState('table');
  const [filterAssignee, setFilterAssignee] = useState(null);
  const [filterProject, setFilterProject] = useState(null);
  const [filterCurator, setFilterCurator] = useState(null);
  const [ganttData, setGanttData] = useState([]);
  const [ganttLoading, setGanttLoading] = useState(false);

  const fetchTasks = () => {
    setLoading(true);
    TasksService.getTasks().then(r => setTasks(r.data)).finally(() => setLoading(false));
  };

  const fetchGantt = () => {
    setGanttLoading(true);
    TasksService.getMyGantt().then(r => {
      console.log("Gantt data received:", r.data);
      setGanttData(r.data);
    }).catch(e => console.error("Error fetching gantt:", e)).finally(() => setGanttLoading(false));
  };

  const fetchProjects = () => {
    ProjectsService.getProjects().then(r => setProjects(r.data));
  };

  const fetchUsers = () => {
    UsersService.getUsers()
      .then(r => setUsers(r.data))
      .catch(err => {
        console.error('Failed to fetch users:', err);
        message.error('Не удалось загрузить список пользователей');
      });
  };

  const avatarUrl = (avatarId) => avatarId ? `/api/v1/files/${avatarId}/content` : null;

  useEffect(() => {
    localStorage.setItem('tasks_table_columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    localStorage.setItem('tasks_show_completed', String(showCompletedTasks));
  }, [showCompletedTasks]);

  useEffect(() => {
    if (!user && !authLoading) {
      navigate('/login');
      return;
    }
    
    if (user) {
      fetchTasks();
      fetchGantt();
      fetchProjects();
      fetchUsers();
    }
  }, [user, authLoading]);

  // Handle URL query parameter to open specific task
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const taskId = urlParams.get('taskId');
    if (taskId && !detailModalOpen) {
      const task = tasks.find(t => t.id === parseInt(taskId));
      if (task) {
        handleViewTaskDetails(task);
      } else if (!loading) {
        // Task not in list (e.g. beyond limit=100) — fetch directly by ID
        TasksService.getTaskById(parseInt(taskId))
          .then(r => {
            setSelectedTask(r.data);
            setDetailModalOpen(true);
          })
          .catch(() => message.error('Задача не найдена'));
      }
    }
  }, [loading, tasks]);

  // --- Project Handlers ---
  const handleCreateProject = async (values) => {
    if (!user) {
      message.error('Требуется авторизация');
      navigate('/login');
      return;
    }
    
    const curators = values.curators || [];
    const managers = values.managers || [];
    const participantsList = values.participants || [];
    
    const formattedParticipants = [
      ...managers.map(id => ({ user_id: id, role: 'manager' })),
      ...curators.map(id => ({ user_id: id, role: 'curator' })),
      ...participantsList.map(id => ({ user_id: id, role: 'participant' }))
    ];

    const payload = {
      ...values,
      start_date: values.start_date?.format('YYYY-MM-DD'),
      end_date: values.end_date?.format('YYYY-MM-DD'),
      participants: formattedParticipants,
    };
    delete payload.curators;
    delete payload.managers;
    
    try {
      await ProjectsService.createProject(payload);
      message.success('Проект создан');
      setProjectModalOpen(false);
      projectForm.resetFields();
      fetchProjects();
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        message.error('Требуется авторизация');
        navigate('/login');
      } else {
        message.error('Ошибка создания проекта: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleEditProject = async (values) => {
    if (!selectedProject) return;
    
    const curators = values.curators || [];
    const managers = values.managers || [];
    const participantsList = values.participants || [];
    
    const formattedParticipants = [
      ...managers.map(id => ({ user_id: id, role: 'manager' })),
      ...curators.map(id => ({ user_id: id, role: 'curator' })),
      ...participantsList.map(id => ({ user_id: id, role: 'participant' }))
    ];

    const payload = {
      end_date: values.end_date?.format('YYYY-MM-DD') || null,
      participants: formattedParticipants,
      comment: values.comment
    };
    
    try {
      await ProjectsService.updateProject(selectedProject.id, payload);
      message.success('Параметры проекта обновлены');
      setEditProjectModalOpen(false);
      editProjectForm.resetFields();
      fetchProjects();
      const response = await ProjectsService.getProjectById(selectedProject.id);
      setSelectedProject(response.data);
    } catch (error) {
      message.error('Ошибка редактирования проекта: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleViewProjectDetails = async (project) => {
    try {
      const response = await ProjectsService.getProjectById(project.id);
      setSelectedProject(response.data);
    } catch (error) {
      console.warn('Failed to fetch full project details, using list data');
      setSelectedProject(project);
    }
    setProjectDetailModalOpen(true);
  };

  // --- Task Handlers ---
  const handleCreateTask = async (values) => {
    if (!user) {
      message.error('Требуется авторизация');
      navigate('/login');
      return;
    }
    if (creating) return;
    
    setCreating(true);
    
    const files = values.files || [];
    const payload = {
      ...values,
      start_date: values.start_date?.format('YYYY-MM-DD'),
      due_date: values.due_date?.format('YYYY-MM-DD'),
      crash_date: values.crash_date?.format('YYYY-MM-DD'),
      initiator_id: values.initiator_id || user.id,
      curator_id: values.curator_id,
      parent_id: parentTask ? parentTask.id : undefined,
    };
    
    delete payload.files;
    
    try {
      const res = await TasksService.createTask(payload);
      const newTask = res.data;
      
      if (files.length > 0) {
        for (const fileObj of files) {
          const formData = new FormData();
          formData.append('content', 'Прикреплен файл');
          formData.append('file', fileObj.originFileObj || fileObj);
          formData.append('title', fileObj.name);
          formData.append('file_type', 'document');
          if (payload.project_id) formData.append('project_id', payload.project_id);
          
          try {
            await TasksService.addCommentWithFile(newTask.id, formData);
          } catch (fileErr) {
            console.error('File upload error:', fileErr);
            message.error(`Ошибка при загрузке файла ${fileObj.name}`);
          }
        }
      }
      
      message.success('Задача создана');
      setModalOpen(false);
      setParentTask(null);
      form.resetFields();
      fetchTasks();
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        message.error('Требуется авторизация');
        navigate('/login');
      } else {
        message.error('Ошибка создания задачи: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setCreating(false);
    }
  };

  const handleViewTaskDetails = async (task) => {
    try {
      try {
        const response = await TasksService.getTaskById(task.id);
        setSelectedTask(response.data);
      } catch (apiError) {
        const enhancedTask = {
          ...task,
          participants: [],
          subtasks: [],
          comments: [],
          attachments: [],
          history: [],
          dependency_tasks: [],
          dependent_tasks: []
        };
        setSelectedTask(enhancedTask);
      }
      setDetailModalOpen(true);
    } catch (error) {
      message.error('Не удалось загрузить детали задачи');
      setSelectedTask(task);
      setDetailModalOpen(true);
    }
  };

  const handleOpenCreateSubtask = () => {
    setParentTask(selectedTask);
    setDetailModalOpen(false);
    form.resetFields();
    form.setFieldsValue({
      project_id: selectedTask.project_id,
      task_type: selectedTask.task_type,
      initiator_id: user ? user.id : undefined
    });
    setModalOpen(true);
  };

  const handleUpdateTask = async (values) => {
    if (!selectedTask) return;
    const payload = {
      ...values,
      start_date: values.start_date?.format('YYYY-MM-DD'),
      due_date: values.due_date?.format('YYYY-MM-DD'),
      crash_date: values.crash_date?.format('YYYY-MM-DD'),
      actual_due_date: values.actual_due_date?.format('YYYY-MM-DD'),
    };
    await TasksService.updateTask(selectedTask.id, payload);
    message.success('Задача обновлена');
    setDetailModalOpen(false);
    fetchTasks();
  };

  const handleAddProjectComment = async () => {
    if (!projectCommentText.trim() || !selectedProject) return;
    try {
      await ProjectsService.addComment(selectedProject.id, { content: projectCommentText });
      message.success('Комментарий добавлен');
      setProjectCommentText('');
      // Refresh project details to fetch comments
      const response = await ProjectsService.getProjectById(selectedProject.id);
      setSelectedProject(response.data);
    } catch (error) {
      message.error('Ошибка добавления комментария к проекту');
    }
  };

  const projectColumns = [
    { 
      title: 'Название', 
      dataIndex: 'name', 
      key: 'name',
      render: (text, record) => (
        <Button type="link" onClick={() => handleViewProjectDetails(record)}>
          {text}
        </Button>
      )
    },
    { title: 'Бюджет', dataIndex: 'budget', key: 'budget', render: (v) => v ? `${v} ₽` : '-' },
    { title: 'Дата начала', dataIndex: 'start_date', key: 'start_date', render: (date) => date ? dayjs(date).format('DD.MM.YYYY') : '-' },
    { title: 'Дата окончания', dataIndex: 'end_date', key: 'end_date', render: (date) => date ? dayjs(date).format('DD.MM.YYYY') : '-' },
  ];

  const taskColumns = [
    { 
      title: 'Название', 
      dataIndex: 'title', 
      key: 'title',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Поиск по названию"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Найти</Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>Сброс</Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => record.title?.toLowerCase().includes(value.toLowerCase()),
      sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => handleViewTaskDetails(record)}
          style={{ 
            textDecoration: (record.status === 'done' || record.status === 'cancelled') ? 'line-through' : 'none',
            color: (record.status === 'done' || record.status === 'cancelled') ? 'gray' : undefined 
          }}
        >
          {text}
        </Button>
      )
    },
    {
      title: 'Тип', 
      dataIndex: 'task_type', 
      key: 'task_type',
      filters: taskTypeOptions.map(opt => ({ text: opt.label, value: opt.value })),
      onFilter: (value, record) => record.task_type === value,
      sorter: (a, b) => {
        const aLabel = taskTypeOptions.find(opt => opt.value === a.task_type)?.label || a.task_type || '';
        const bLabel = taskTypeOptions.find(opt => opt.value === b.task_type)?.label || b.task_type || '';
        return aLabel.localeCompare(bLabel);
      },
      render: (type) => {
        const option = taskTypeOptions.find(opt => opt.value === type);
        return option ? option.label : type;
      }
    },
    {
      title: 'Статус', 
      dataIndex: 'status', 
      key: 'status',
      filters: statusOptions.map(opt => ({ text: opt.label, value: opt.value })),
      onFilter: (value, record) => record.status === value,
      sorter: (a, b) => {
        const aLabel = statusOptions.find(opt => opt.value === a.status)?.label || a.status || '';
        const bLabel = statusOptions.find(opt => opt.value === b.status)?.label || b.status || '';
        return aLabel.localeCompare(bLabel);
      },
      render: (s, record) => (
        <Select 
          size="small" 
          value={s} 
          options={statusOptions} 
          onChange={(v) => handleStatusChange(record.id, v)} 
          style={{ width: 130 }} 
        />
      ),
    },
    {
      title: 'Приоритет', 
      dataIndex: 'priority', 
      key: 'priority',
      filters: priorityOptions.map(opt => ({ text: opt.label, value: opt.value })),
      onFilter: (value, record) => record.priority === value,
      sorter: (a, b) => {
        const aLabel = priorityOptions.find(opt => opt.value === a.priority)?.label || a.priority || '';
        const bLabel = priorityOptions.find(opt => opt.value === b.priority)?.label || b.priority || '';
        return aLabel.localeCompare(bLabel);
      },
      render: (p) => {
        const colors = { low: 'blue', medium: 'orange', high: 'red', critical: 'magenta' };
        return <Tag color={colors[p]}>{priorityOptions.find(opt => opt.value === p)?.label || p}</Tag>;
      },
    },
    { 
      title: 'Проект', 
      dataIndex: 'project_id', 
      key: 'project_id',
      filters: projects.map(p => ({ text: p.name, value: p.id })),
      onFilter: (value, record) => record.project_id === value,
      sorter: (a, b) => {
        const aName = projects.find(p => String(p.id) === String(a.project_id))?.name || '';
        const bName = projects.find(p => String(p.id) === String(b.project_id))?.name || '';
        return aName.localeCompare(bName);
      },
      render: (projectId) => {
        const project = projects.find(p => String(p.id) === String(projectId));
        return project ? project.name : `ID: ${projectId}`;
      }
    },
    { 
      title: 'Исполнитель', 
      dataIndex: 'assignee_id', 
      key: 'assignee_id',
      filters: users.map(u => ({ text: u.full_name || u.email, value: u.id })),
      onFilter: (value, record) => record.assignee_id === value,
      sorter: (a, b) => {
        const aName = a.assignee_name || users.find(u => String(u.id) === String(a.assignee_id))?.full_name || '';
        const bName = b.assignee_name || users.find(u => String(u.id) === String(b.assignee_id))?.full_name || '';
        return aName.localeCompare(bName);
      },
      render: (assigneeId) => {
        const user = users.find(u => String(u.id) === String(assigneeId));
        return user ? (user.full_name || user.email) : (assigneeId ? `ID: ${assigneeId}` : 'Не назначен');
      }
    },
    { 
      title: 'Постановщик', 
      dataIndex: 'initiator_id', 
      key: 'initiator_id',
      filters: users.map(u => ({ text: u.full_name || u.email, value: u.id })),
      onFilter: (value, record) => record.initiator_id === value,
      sorter: (a, b) => {
        const aName = a.initiator_name || users.find(u => String(u.id) === String(a.initiator_id))?.full_name || '';
        const bName = b.initiator_name || users.find(u => String(u.id) === String(b.initiator_id))?.full_name || '';
        return aName.localeCompare(bName);
      },
      render: (initiatorId) => {
        const user = users.find(u => String(u.id) === String(initiatorId));
        return user ? (user.full_name || user.email) : `ID: ${initiatorId}`;
      }
    },
    { 
      title: 'Куратор', 
      dataIndex: 'curator_id', 
      key: 'curator_id',
      filters: users.map(u => ({ text: u.full_name || u.email, value: u.id })),
      onFilter: (value, record) => record.curator_id === value,
      sorter: (a, b) => {
        const aName = a.curator_name || users.find(u => String(u.id) === String(a.curator_id))?.full_name || '';
        const bName = b.curator_name || users.find(u => String(u.id) === String(b.curator_id))?.full_name || '';
        return aName.localeCompare(bName);
      },
      render: (curatorId) => {
        const user = users.find(u => String(u.id) === String(curatorId));
        return user ? (user.full_name || user.email) : (curatorId ? `ID: ${curatorId}` : 'Не назначен');
      }
    },
    { 
      title: 'Прогресс', 
      dataIndex: 'progress', 
      key: 'progress', 
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="Поиск %"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Найти</Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>Сброс</Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => String(record.progress || 0).includes(value),
      sorter: (a, b) => (a.progress || 0) - (b.progress || 0),
      render: (v) => `${v}%` 
    },
    { 
      title: 'Срок', 
      dataIndex: 'due_date', 
      key: 'due_date',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="ДД.ММ.ГГГГ"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Найти</Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>Сброс</Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => {
        const dateToFilter = record.actual_due_date || record.due_date;
        if (!dateToFilter) return false;
        return dayjs(dateToFilter).format('DD.MM.YYYY').includes(value);
      },
      sorter: (a, b) => new Date(a.actual_due_date || a.due_date || 0) - new Date(b.actual_due_date || b.due_date || 0),
      render: (_, record) => {
        const dateToRender = record.actual_due_date || record.due_date;
        return dateToRender ? dayjs(dateToRender).format('DD.MM.YYYY') : '-';
      }
    },
    {
      title: 'Активность',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: (a, b) => new Date(a.updated_at || 0) - new Date(b.updated_at || 0),
      render: (date) => {
        if (!date) return '-';
        const d = dayjs(date);
        return d.isSame(dayjs(), 'day') ? d.format('HH:mm') : d.format('DD.MM.YYYY HH:mm');
      }
    },
    { 
      title: 'Крах', 
      dataIndex: 'crash_date', 
      key: 'crash_date',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            placeholder="ДД.ММ.ГГГГ"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Найти</Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>Сброс</Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => {
        if (!record.crash_date) return false;
        return dayjs(record.crash_date).format('DD.MM.YYYY').includes(value);
      },
      sorter: (a, b) => new Date(a.crash_date || 0) - new Date(b.crash_date || 0),
      render: (date) => date ? dayjs(date).format('DD.MM.YYYY') : '-'
    },
    { 
      title: 'Блокировка', 
      dataIndex: 'is_blocked', 
      key: 'is_blocked',
      filters: [
        { text: 'Заблокирована', value: true },
        { text: 'Активна', value: false }
      ],
      onFilter: (value, record) => !!record.is_blocked === value,
      sorter: (a, b) => (a.is_blocked === b.is_blocked ? 0 : a.is_blocked ? -1 : 1),
      render: (blocked) => (
        <Tag color={blocked ? 'red' : 'green'}>
          {blocked ? 'Заблокирована' : 'Активна'}
        </Tag>
      )
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" onClick={() => handleViewTaskDetails(record)}>
            Подробнее
          </Button>
        </Space>
      ),
    },
  ];

  const buildTaskTree = (flatTasks) => {
    const taskMap = {};
    const roots = [];

    // First pass: map all tasks and initialize children array
    flatTasks.forEach(task => {
      taskMap[task.id] = { ...task, children: [] };
    });

    // Second pass: attach children to parents
    flatTasks.forEach(task => {
      if (task.parent_id && taskMap[task.parent_id]) {
        taskMap[task.parent_id].children.push(taskMap[task.id]);
      } else {
        roots.push(taskMap[task.id]);
      }
    });

    // Clean up empty children arrays so AntD doesn't show expand icon for leaves
    const cleanEmptyChildren = (nodes) => {
      nodes.forEach(node => {
        if (node.children.length === 0) {
          delete node.children;
        } else {
          cleanEmptyChildren(node.children);
        }
      });
    };
    
    cleanEmptyChildren(roots);
    return roots;
  };

  let visibleTasksList = showCompletedTasks ? tasks : tasks.filter(t => t.status !== 'done');
  
  if (filterAssignee) {
    visibleTasksList = visibleTasksList.filter(t => t.assignee_id === filterAssignee);
  }
  if (filterProject) {
    visibleTasksList = visibleTasksList.filter(t => t.project_id === filterProject);
  }
  if (filterCurator) {
    visibleTasksList = visibleTasksList.filter(t => t.curator_id === filterCurator);
  }

  const filteredTasks = buildTaskTree(visibleTasksList);
  const filteredColumns = taskColumns.filter(col => visibleColumns.includes(col.key));

  const dateCellRender = (value) => {
    const listData = visibleTasksList.filter(task => {
      const dateToFilter = task.actual_due_date || task.due_date;
      if (!dateToFilter) return false;
      return dayjs(dateToFilter).isSame(value, 'day');
    });

    return (
      <ul className="events" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {listData.map((item) => {
          let status = 'default';
          if (item.status === 'done') status = 'success';
          else if (item.status === 'in_progress') status = 'processing';
          else if (item.is_blocked) status = 'error';
          else if (item.status === 'review') status = 'warning';
          
          return (
            <li key={item.id} onClick={(e) => { e.stopPropagation(); handleViewTaskDetails(item); }} style={{ cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <Badge status={status} text={<span style={{ fontSize: '12px' }}>{item.title}</span>} />
            </li>
          );
        })}
      </ul>
    );
  };

  const renderDiaryCard = (task) => {
    const pOpt = priorityOptions.find(o => o.value === task.priority);
    const pLabel = pOpt ? pOpt.label : task.priority;
    const pColors = { low: 'blue', medium: 'orange', high: 'red', critical: 'magenta' };
    const assigneeUser = users.find(u => String(u.id) === String(task.assignee_id));
    const project = projects.find(p => String(p.id) === String(task.project_id));
    const targetDate = task.actual_due_date || task.due_date;
    
    return (
      <Card
        key={task.id}
        size="small"
        style={{ marginBottom: 8, cursor: 'pointer', borderRadius: 12, border: '1px solid #e2e8f0' }}
        onClick={() => handleViewTaskDetails(task)}
        className="card-hover"
        title={
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 13, fontWeight: 500 }}>
            {task.title}
          </div>
        }
        extra={<Tag color={pColors[task.priority] || 'default'} style={{ borderRadius: 6, fontSize: 11 }}>{pLabel}</Tag>}
        styles={{ body: { padding: '8px 12px' } }}
      >
        {project && <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{project.name}</div>}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: '#64748b' }}>
          <span><UserOutlined /> {(task.assignee_name || (assigneeUser ? (assigneeUser.full_name || assigneeUser.email) : null) || (task.assignee_id ? `ID: ${task.assignee_id}` : null))?.split(' ')[0] || '—'}</span>
          <span><CalendarOutlined /> {targetDate ? dayjs(targetDate).format('DD.MM') : '—'}</span>
        </div>
      </Card>
    );
  };

  const renderDiaryView = () => {
    const today = dayjs().startOf('day');
    
    const overdue = [];
    const todayTasks = [];
    const tomorrowTasks = [];
    const next5DaysTasks = [];

    visibleTasksList.forEach(task => {
      const targetDateStr = task.actual_due_date || task.due_date;
      if (!targetDateStr) return;
      
      const targetDate = dayjs(targetDateStr).startOf('day');
      const diff = targetDate.diff(today, 'day');

      if (diff < 0) overdue.push(task);
      else if (diff === 0) todayTasks.push(task);
      else if (diff === 1) tomorrowTasks.push(task);
      else if (diff >= 2 && diff <= 6) next5DaysTasks.push(task);
    });

    const columnStyle = (bgColor, headerColor) => ({
      backgroundColor: bgColor,
      height: 'calc(100vh - 320px)',
      overflowY: 'auto',
      borderRadius: 16,
    });

    const headerStyle = (color) => ({
      backgroundColor: color,
      borderRadius: '16px 16px 0 0',
      padding: '12px 16px',
      fontWeight: 600,
      fontSize: 14,
    });

    return (
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card 
            title={<span style={{ color: '#ef4444' }}>Просрочено ({overdue.length})</span>}
            style={columnStyle('#fef2f2', '#ef4444')}
            styles={{ header: headerStyle('#fee2e2'), body: { padding: 12 } }}
          >
            {overdue.map(renderDiaryCard)}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card 
            title={<span style={{ color: '#10b981' }}>Сегодня ({todayTasks.length})</span>}
            style={columnStyle('#f0fdf4', '#10b981')}
            styles={{ header: headerStyle('#dcfce7'), body: { padding: 12 } }}
          >
            {todayTasks.map(renderDiaryCard)}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card 
            title={<span style={{ color: '#06b6d4' }}>Завтра ({tomorrowTasks.length})</span>}
            style={columnStyle('#ecfeff', '#06b6d4')}
            styles={{ header: headerStyle('#cffafe'), body: { padding: 12 } }}
          >
            {tomorrowTasks.map(renderDiaryCard)}
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card 
            title={<span style={{ color: '#6366f1' }}>5 дней ({next5DaysTasks.length})</span>}
            style={columnStyle('#eef2ff', '#6366f1')}
            styles={{ header: headerStyle('#e0e7ff'), body: { padding: 12 } }}
          >
            {next5DaysTasks.map(renderDiaryCard)}
          </Card>
        </Col>
      </Row>
    );
  };

  const cellRender = (current, info) => {
    if (info.type === 'date') return dateCellRender(current);
    return info.originNode;
  };

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title">Задачи</h1>
          <p className="page-subtitle">Управление задачами и проектами</p>
        </div>
      </div>
      
      <Card style={{ marginBottom: 20, borderRadius: 16 }} styles={{ body: { padding: '16px 20px' } }}>
        <Space style={{ flexWrap: 'wrap', width: '100%' }} size={[12, 12]}>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => {
              form.resetFields();
              if (user) {
                form.setFieldsValue({ initiator_id: user.id });
              }
              setModalOpen(true);
            }}
            style={{ 
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              border: 'none',
              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
            }}
          >
            Новая задача
          </Button>
          <Checkbox 
            checked={showCompletedTasks} 
            onChange={(e) => setShowCompletedTasks(e.target.checked)}
          >
            Завершённые
          </Checkbox>
          <Popover
            content={
              <Checkbox.Group
                style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                value={visibleColumns}
                onChange={setVisibleColumns}
              >
                {taskColumns.map(col => (
                  <Checkbox key={col.key} value={col.key}>
                    {col.title}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            }
            title="Колонки"
            trigger="click"
            placement="bottomLeft"
          >
            <Button icon={<SettingOutlined />}>Колонки</Button>
          </Popover>
          <Select 
            value={viewMode} 
            onChange={setViewMode} 
            options={[
              { value: 'table', label: 'Таблица' },
              { value: 'gantt', label: 'Гант' },
              { value: 'calendar', label: 'Календарь' },
              { value: 'diary', label: 'Ежедневник' }
            ]} 
            style={{ width: 140 }}
          />
          <Select
            allowClear
            showSearch
            placeholder="Исполнитель"
            value={filterAssignee}
            onChange={setFilterAssignee}
            options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
            style={{ width: 160 }}
            filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
          />
          <Select
            allowClear
            showSearch
            placeholder="Проект"
            value={filterProject}
            onChange={setFilterProject}
            options={projects.map(p => ({ value: p.id, label: p.name }))}
            style={{ width: 160 }}
            filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
          />
          <Select
            allowClear
            showSearch
            placeholder="Куратор"
            value={filterCurator}
            onChange={setFilterCurator}
            options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
            style={{ width: 160 }}
            filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
          />
        </Space>
      </Card>
      
      {viewMode === 'table' ? (
        <>
          <style>{`
            .super-compact-table .ant-table-tbody > tr > td {
              padding: 4px 8px !important;
              font-size: 13px !important;
            }
            .super-compact-table .ant-table-thead > tr > th {
              padding: 8px !important;
              font-size: 12px !important;
              font-weight: 600 !important;
              color: #64748b !important;
              background: #f8fafc !important;
              border-bottom: 1px solid #e2e8f0 !important;
            }
            .super-compact-table .ant-table-tbody > tr:hover > td {
              background: #f1f5f9 !important;
            }
            .super-compact-table .ant-select-selector {
              height: 24px !important;
              min-height: 24px !important;
              padding: 0 6px !important;
              border-radius: 6px !important;
            }
            .super-compact-table .ant-select-selection-item {
              line-height: 22px !important;
            }
            .super-compact-table .ant-btn-sm {
              height: 24px !important;
              padding: 0 8px !important;
              font-size: 11px !important;
              border-radius: 6px !important;
            }
            .super-compact-table .ant-tag {
              line-height: 18px !important;
              padding: 0 6px !important;
              font-size: 11px !important;
              margin-right: 0 !important;
              border-radius: 6px !important;
            }
          `}</style>
          <Card style={{ borderRadius: 16 }} styles={{ body: { padding: 0 } }}>
            <Table 
              className="super-compact-table" 
              dataSource={filteredTasks} 
              columns={filteredColumns} 
              rowKey="id" 
              loading={loading} 
              scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }} 
              size="small" 
              pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200', '500'] }}
            />
          </Card>
        </>
      ) : viewMode === 'gantt' ? (
        <Card style={{ borderRadius: 16 }}>
          <div style={{ position: 'relative' }}>
            {ganttLoading && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.7)', zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: 16 }}>Загрузка...</div>}
            <GanttChartWrapper 
              ganttData={ganttData} 
              onTaskClick={(task) => {
                const fullTask = tasks.find(t => t.id === task.id);
                if (fullTask) handleViewTaskDetails(fullTask);
              }} 
            />
          </div>
        </Card>
      ) : viewMode === 'diary' ? (
        <div style={{ padding: '0' }}>
          {renderDiaryView()}
        </div>
      ) : (
        <Card style={{ borderRadius: 16 }}>
          <style>{`
            .ant-picker-calendar-date-content {
              height: 100px !important;
              overflow-y: auto !important;
            }
          `}</style>
          <Calendar cellRender={cellRender} />
        </Card>
      )}

      {/* Модальное окно создания задачи */}
      <TaskCreateModal
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setParentTask(null);
          form.resetFields();
        }}
        form={form}
        onFinish={handleCreateTask}
        users={users}
        projects={projects}
        tasks={tasks}
        parentTask={parentTask}
        taskTypeOptions={taskTypeOptions}
        priorityOptions={priorityOptions}
        user={user}
        confirmLoading={creating}
      />

      
      <TaskDetailModal 
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        selectedTask={selectedTask}
        setSelectedTask={setSelectedTask}
        users={users}
        projects={projects}
        tasks={tasks}
        onUpdate={fetchTasks}
        onOpenCreateSubtask={handleOpenCreateSubtask}
      />
</div>
  );
}
